import React, { useEffect, useRef, useState } from 'react';
import { debounce, find, isEqual } from 'lodash';

import usePathname from '@magoosh/utils/use_pathname';

import * as api from './api';
import { Activity, Interaction, InteractionPart, TutoringSession } from './types';

const AITutorContext = React.createContext<any>(null);

export const useAITutorContext = () => {
  const context = React.useContext(AITutorContext);
  if (context === undefined) {
    throw new Error('You must wrap your page with <AITutorProvider /> in order to useAITutorContext().');
  }
  return context;
};

interface Props {
  children: any;
  initialData: {
    scrollTop?: number;
    tutoringSessionsStack?: TutoringSession[];
    width?: number;
  }
}

export const AITutorProvider: React.FC<Props> = (props) => {
  const pathname = usePathname();
  const resizeableContainerRef = useRef(null);

  const [activity, setActivity] = useState<Activity>(null);
  const [persistedTutoringUIState, setPersistedTutoringUIState] = useState(null);
  const [scrollTop, setScrollTop] = useState(props.initialData.scrollTop || 0);
  const [structuredPageContext, setStructuredPageContext] = useState({});
  const [tutorActive, setTutorActive] = useState<boolean>(!!props.initialData.tutoringSessionsStack.length);
  const [tutoringSessionsStack, setTutoringSessionsStack] = useState<TutoringSession[]>(
    props.initialData.tutoringSessionsStack || []
  );
  const [width, setWidth] = useState<number>(props.initialData.width || 425);

  const debouncedSaveTutoringUIState = useRef(
    debounce((uiState) => {
      setPersistedTutoringUIState(uiState);
      localStorage.setItem('aiTutor.uiState', JSON.stringify({ ...uiState, tutoringSessionsStack }));
      api.saveAITutoringUIState(uiState);
    }, 500)
  ).current;

  useEffect(() => {
    setStructuredPageContext({});
  }, [pathname]);

  useEffect(() => {
    const restoreUIState = (e) => {
      if (e.persisted) {
        // User used the back/forward buttons, restore the UI state in case the browser cached
        //version is old
        try {
          const storedUIState = JSON.parse(localStorage.getItem('aiTutor.uiState'));
          setTutoringSessionsStack(storedUIState.tutoringSessionsStack);
          setPersistedTutoringUIState({
            ...storedUIState,
            tutoringSessionsStack: storedUIState.tutoringSessionsStack.map((tutoringSession) =>
              tutoringSession.id
            )
          })
        } catch {
          // No-op
        }
      }
    };

    const updateStructuredContext = (e) => {
      setStructuredPageContext({ ...structuredPageContext, ...e.detail });
    };

    document.addEventListener('aiTutor.structuredContext', updateStructuredContext);
    window.addEventListener('pageshow', restoreUIState);

    return () => {
      document.removeEventListener('aiTutor.structuredContext', updateStructuredContext);
      window.removeEventListener('pageshow', restoreUIState);
    }
  }, []);

  useEffect(() => {
    if (tutoringSessionsStack.length) {
      showTutor();
    }
  }, [tutoringSessionsStack]);

  useEffect(() => {
    // Avoid storing UI state when the tutor is not active.
    if (tutorActive || !!persistedTutoringUIState?.tutoringSessionsStack?.length) {
      handleUIStateChanged();
    }
  }, [scrollTop, tutoringSessionsStack, tutoringSessionsStack[0]?.interactions, width]);

  const clearActivity = () => {
    setActivity(null);
  };

  const clearStack = () => {
    clearActivity();
    setTutoringSessionsStack([]);
  };

  const currentContext = () => {
    if (typeof document !== 'undefined') {
      return {
        page: {
          raw: document.getElementById('resizeable-container')?.innerText,
          structured: structuredPageContext,
          title: document.title.split(' - ').slice(0, -1).join(' - ').replace('<dev> ', '')
        },
        path: pathname
      };
    } else {
      return {
        page: {
          raw: null,
          structured: structuredPageContext,
          title: null
        },
        path: pathname
      }
    }
  };

  const handleNewInteractions = (interactions: Interaction[]) => {
    // Ensure we have the current state, even if this is called from a closure
    setTutoringSessionsStack((tutoringSessions) => {
      const tutoringSession = tutoringSessions.at(-1);
      if (!tutoringSession) return [];

      // We may get more recent content for an interaction we already have
      // (e.g. new streamed chat content). Take the new content.
      // Drop provisional content that was added purely for quick UI feedback
      const nonProvisionalInteractions = tutoringSession.interactions.filter((interaction) => !interaction.provisional);
      const updatedExistingInteractions = nonProvisionalInteractions.map((existingInteraction) => {
        const updatedInteraction = find(interactions, (interaction) => interaction.id === existingInteraction.id);
        return updatedInteraction || existingInteraction;
      })

      const newInteractions = interactions.filter((interaction) => {
        return !find(tutoringSession.interactions, (existingInteraction) => interaction.id === existingInteraction.id)
      });

      return [
        ...tutoringSessions.slice(0, -1),
        {
          ...tutoringSession,
          interactions: [
            ...updatedExistingInteractions,
            ...newInteractions
          ]
        }
      ];
    });

    // If the updated interactions include an activity, update it as well
    let newActivity = null;
    interactions.some((interaction) => {
      return interaction.parts.some((interactionPart) => {
        if (interactionPart.isActivity) {
          newActivity = { interaction, interactionPart };
          return true;
        } else {
          return false;
        }
      });
    });

    if (newActivity) setActivity(newActivity);
  };

  const handleUIStateChanged = () => {
    if (!resizeableContainerRef.current) {
      resizeableContainerRef.current = document.getElementById('resizeable-container');
    }

    const tutoringUIState = {
      resizeableContainer: resizeableContainerRef.current ? {
        marginLeft: resizeableContainerRef.current.style.marginLeft,
        maxWidth: resizeableContainerRef.current.style.maxWidth
      } : {
        marginLeft: 'auto',
        maxWidth: 'none'
      },
      scrollTop,
      tutoringSessionsStack: tutoringSessionsStack.map((tutoringSession) => tutoringSession.id),
      width
    };

    if (!isEqual(tutoringUIState, persistedTutoringUIState)) {
      debouncedSaveTutoringUIState(tutoringUIState);
    }
  };

  const hideTutor = () => {
    setTutorActive(false);
    clearStack();
  };

  const popFromStack = () => {
    clearActivity();
    setTutoringSessionsStack((tutoringSessions) => tutoringSessions.slice(0, -1));
  };

  const pushToStack = (tutoringSession: TutoringSession) => {
    setTutorActive(true);
    clearActivity();
    setTutoringSessionsStack((tutoringSessions) => [...tutoringSessions, tutoringSession]);
  };

  const replaceTopOfStack = (tutoringSession: TutoringSession) => {
    setTutoringSessionsStack((tutoringSessions) => [...tutoringSessions.slice(0, -1), tutoringSession]);
  };

  const showTutor = () => {
    setTutorActive(true);
  };

  const providerValue = {
    activity,
    clearActivity,
    clearStack,
    currentContext,
    handleNewInteractions,
    hideTutor,
    popFromStack,
    pushToStack,
    replaceTopOfStack,
    scrollTop,
    setActivity,
    setScrollTop,
    setWidth,
    showTutor,
    tutorActive,
    tutoringSessionsStack,
    width
  };

  return <AITutorContext.Provider value={providerValue}>{props.children}</AITutorContext.Provider>;
};
